import { type FormEvent, useEffect, useState } from 'react';
import { type LeaderboardResponse, POINTS } from '@notlost/shared';
import { Logo, PinIcon, ShieldCheck } from '../components/Brand';
import { Footer, Header } from '../components/Layout';
import { ApiError, createDemoTag, getLeaderboard } from '../lib/api';
import { loadDemo, saveDemo } from '../lib/demo';
import { navigate } from '../lib/router';
import { t } from '../i18n';

/**
 * Apple-style landing page: one idea per full-width section, big centered
 * headlines, alternating white, light grey and black backgrounds.
 */
export function Home() {
  return (
    <div className="flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1">
        <Hero />
        <Privacy />
        <HowItWorks />
        <Helpers />
        <Demo />
      </main>
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="bg-white px-5 pt-14 text-center sm:pt-20">
      <p className="eyebrow">{t('home.eyebrow')}</p>
      <h1 className="headline mx-auto mt-3 max-w-3xl text-[44px] sm:text-[64px] lg:text-[80px]">{t('home.title')}</h1>
      <p className="mx-auto mt-4 max-w-2xl text-[21px] leading-snug text-body sm:text-[28px]">{t('home.subtitle')}</p>
      <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
        <a href="#demo" className="btn btn-primary min-w-44">
          {t('home.cta')}
        </a>
        <button type="button" onClick={() => navigate('/code')} className="btn btn-secondary min-w-44">
          {t('home.ctaCode')}
        </button>
      </div>
      <PhoneMock />
    </section>
  );
}

/** Product shot: the ping a parent receives. Decorative. */
function PhoneMock() {
  return (
    <div aria-hidden="true" className="relative mx-auto mt-14 w-[300px] sm:w-[340px]">
      <div className="rounded-t-[54px] bg-ink px-3.5 pt-3.5 shadow-lift">
        <div className="overflow-hidden rounded-t-[42px] bg-surface">
          <div className="mx-auto mt-3 h-7 w-28 rounded-full bg-ink" />
          <div className="mx-4 mt-6 rounded-[22px] bg-white/90 p-4 text-left shadow-card backdrop-blur">
            <div className="flex items-center gap-2 text-[13px] text-muted">
              <Logo size={18} />
              <span className="font-semibold text-ink">not lost.</span>
              <span className="ml-auto">{t('home.mock.now')}</span>
            </div>
            <p className="mt-2 text-[15px] font-semibold text-ink">{t('home.mock.title')}</p>
            <p className="text-[15px] text-body">{t('home.mock.body')}</p>
          </div>
          <div className="relative mx-4 mt-4 h-52 rounded-[22px] bg-[#e7eef7]">
            <svg viewBox="0 0 300 200" className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
              <path d="M0 140 C60 120 90 160 150 130 S250 90 300 110" stroke="#fff" strokeWidth="14" fill="none" />
              <path d="M110 0 C120 60 100 120 130 200" stroke="#fff" strokeWidth="10" fill="none" />
              <rect x="190" y="20" width="80" height="50" rx="8" fill="#d6e8d2" />
              <rect x="20" y="20" width="60" height="70" rx="8" fill="#dfe6ee" />
            </svg>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full text-blue drop-shadow">
              <PinIcon className="h-11 w-11" />
            </div>
          </div>
          <div className="mx-4 mt-4 pb-6">
            <div className="btn btn-primary w-full text-[15px]">{t('home.mock.button')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Privacy() {
  const items = [
    { key: 'private', icon: <ShieldCheck className="h-9 w-9" /> },
    { key: 'anonymous', icon: <ShieldCheck className="h-9 w-9" /> },
    { key: 'eu', icon: <ShieldCheck className="h-9 w-9" /> },
  ] as const;
  return (
    <section className="bg-surface px-5 py-20 text-center sm:py-28">
      <h2 className="headline mx-auto max-w-3xl text-[40px] sm:text-[56px]">{t('privacy.title')}</h2>
      <p className="mx-auto mt-4 max-w-2xl text-[19px] leading-relaxed text-body sm:text-[21px]">{t('privacy.body')}</p>
      <ul className="mx-auto mt-14 grid max-w-[1024px] gap-5 sm:grid-cols-3">
        {items.map((item) => (
          <li key={item.key} className="tile bg-white px-6 py-10">
            <span className="mx-auto flex h-14 w-14 items-center justify-center text-blue">{item.icon}</span>
            <h3 className="mt-4 text-[21px] font-semibold tracking-tight">{t(`privacy.${item.key}.title`)}</h3>
            <p className="mt-2 text-[17px] leading-snug text-muted">{t(`privacy.${item.key}.body`)}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function HowItWorks() {
  const steps = ['1', '2', '3', '4'] as const;
  return (
    <section id="how" className="scroll-mt-12 bg-white px-5 py-20 sm:py-28">
      <div className="mx-auto max-w-[1024px]">
        <h2 className="headline text-center text-[40px] sm:text-[56px]">{t('how.title')}</h2>
        <ol className="mt-14 grid gap-5 sm:grid-cols-2">
          {steps.map((n) => (
            <li key={n} className="tile flex min-h-64 flex-col justify-between bg-surface p-8 sm:p-10">
              <span className="text-[17px] font-semibold text-muted">{t('how.step', { n })}</span>
              <div>
                <h3 className="headline text-[32px] sm:text-[40px]">{t(`how.${n}.title`)}</h3>
                <p className="mt-3 max-w-md text-[17px] leading-relaxed text-body">{t(`how.${n}.body`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Helpers() {
  const [board, setBoard] = useState<LeaderboardResponse | null>(null);
  useEffect(() => {
    getLeaderboard()
      .then(setBoard)
      .catch(() => setBoard({ entries: [] }));
  }, []);

  return (
    <section className="bg-night px-5 py-20 text-center text-white sm:py-28">
      <p className="text-[17px] font-semibold text-amber">{t('helpers.eyebrow')}</p>
      <h2 className="headline mx-auto mt-3 max-w-3xl text-[40px] sm:text-[56px]">{t('helpers.title')}</h2>
      <p className="mx-auto mt-4 max-w-2xl text-[19px] leading-relaxed text-night-muted sm:text-[21px]">{t('helpers.body')}</p>

      <div className="mx-auto mt-12 grid max-w-[1024px] gap-5 text-left sm:grid-cols-3">
        <div className="tile bg-night-card p-8">
          <p className="headline text-[48px] text-white">+{POINTS.find}</p>
          <p className="mt-2 text-[17px] text-night-muted">{t('helpers.points.findLabel')}</p>
        </div>
        <div className="tile bg-night-card p-8">
          <p className="headline text-[48px] text-amber">+{POINTS.returned}</p>
          <p className="mt-2 text-[17px] text-night-muted">{t('helpers.points.returnLabel')}</p>
        </div>
        <div className="tile bg-night-card p-8 sm:row-span-2">
          <h3 className="text-[17px] font-semibold">{t('helpers.board')}</h3>
          {board && board.entries.length === 0 && <p className="mt-4 text-night-muted">{t('helpers.boardEmpty')}</p>}
          <ol className="mt-4 flex flex-col gap-3">
            {board?.entries.slice(0, 5).map((e, i) => (
              <li key={`${e.nickname}-${i}`} className="flex items-center justify-between">
                <span className="flex items-center gap-3">
                  <span className={`w-5 text-right tabular-nums ${i === 0 ? 'text-amber' : 'text-night-muted'}`}>{i + 1}</span>
                  <span>{e.nickname}</span>
                </span>
                <span className="tabular-nums text-night-muted">{e.points}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="tile bg-night-card p-8 sm:col-span-2">
          <p className="text-[17px] text-night-muted">{t('helpers.levels')}</p>
          <p className="mt-2 text-[21px] font-semibold tracking-tight">Rookie · Spotter · Good Neighbour · Lost & Found Hero · Legend</p>
        </div>
      </div>
      <button type="button" onClick={() => navigate('/helper')} className="chevron-link mt-10 text-night-link">
        {t('helpers.open')}
      </button>
    </section>
  );
}

function Demo() {
  const [saved] = useState(loadDemo);
  const [label, setLabel] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'failed' | 'emailNotAllowed' | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await createDemoTag(label, email);
      saveDemo({ tagId: res.tagId, label: res.label, expiresAt: res.expiresAt }, res.ownerKey);
      // The key travels in the fragment, so it never reaches the server logs.
      window.history.pushState(null, '', `/o/${res.tagId}#k=${res.ownerKey}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'email_not_allowed' ? 'emailNotAllowed' : 'failed');
      setBusy(false);
    }
  }

  return (
    <section id="demo" className="scroll-mt-12 bg-surface px-5 py-20 sm:py-28">
      <div className="mx-auto max-w-[640px] text-center">
        <h2 className="headline text-[40px] sm:text-[56px]">{t('demo.title')}</h2>
        <p className="mx-auto mt-4 max-w-xl text-[19px] leading-relaxed text-body">{t('demo.body')}</p>
        {saved && (
          <button type="button" onClick={() => navigate(`/o/${saved.tagId}`)} className="chevron-link mt-6">
            {t('demo.resume', { label: saved.label })}
          </button>
        )}
      </div>

      <form onSubmit={create} className="tile mx-auto mt-10 flex max-w-[560px] flex-col gap-5 bg-white p-6 text-left sm:p-10">
        <div className="flex flex-col gap-2">
          <label htmlFor="item" className="text-[15px] font-semibold">
            {t('demo.label')}
          </label>
          <input id="item" maxLength={40} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('demo.placeholder')} className="field" />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-[15px] font-semibold">
            {t('demo.email')}
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('demo.emailPlaceholder')}
            className="field"
          />
          <span className="text-[13px] text-muted">{t('demo.emailHint')}</span>
        </div>
        <button type="submit" disabled={busy} className="btn btn-primary mt-2 min-h-12">
          {busy ? t('demo.creating') : t('demo.create')}
        </button>
        {error && (
          <p role="alert" className="text-[15px] text-danger">
            {t(`demo.${error}`)}
          </p>
        )}
      </form>
    </section>
  );
}
